import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import LandingAtmosphere from '../components/ui/LandingAtmosphere';
import ParrotEmblem from '../components/ui/ParrotEmblem';
import { signupApi, type SignupIntentPublic } from '../api/signup';
import { getApiErrorMessage } from '../utils/errorHandling';

const STATUS_COPY: Record<string, string> = {
  draft: 'Draft — finish the signup wizard to continue.',
  awaiting_payment: 'Waiting for payment. Complete Checkout if a payment window did not open.',
  queued_for_provision: 'Queued for provisioning. Automation will create your dedicated instance shortly.',
  provisioning: 'Provisioning is in progress — this page updates automatically.',
  live: 'Your instance is live.',
  rejected: 'This signup was rejected. Contact support if you need help.',
  cancelled: 'This signup was cancelled.',
  expired: 'This signup expired. Start a new signup from the login page.',
};

export default function SignupStatus() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const [intent, setIntent] = useState<SignupIntentPublic | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await signupApi.getIntent(id);
      setIntent(data);
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load signup status'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 15000);
    return () => window.clearInterval(t);
  }, [load]);

  const checkoutHint = params.get('checkout');

  return (
    <div className="marketing-landing relative min-h-screen">
      <LandingAtmosphere className="fixed inset-0" />
      <main className="relative z-10 mx-auto max-w-lg px-4 py-16 sm:px-6">
        <div className="mb-6 flex items-center gap-2.5">
          <ParrotEmblem className="h-9 w-9" animated={false} />
          <span className="marketing-brand-title text-2xl font-bold">BirdMan</span>
        </div>

        <div className="rounded-3xl border border-teal-900/10 bg-white/85 p-7 backdrop-blur-xl">
          <h1 className="marketing-display text-2xl font-semibold text-[var(--bm-landing-ink)]">
            Signup status
          </h1>

          {checkoutHint === 'success' && (
            <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              Payment submitted. Status updates when Stripe confirms the subscription.
            </p>
          )}
          {checkoutHint === 'cancelled' && (
            <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Checkout was cancelled. You can resume payment from a new signup or contact support.
            </p>
          )}

          {loading && <p className="mt-6 text-sm text-slate-500">Loading…</p>}
          {error && <p className="mt-6 text-sm text-rose-600">{error}</p>}

          {intent && (
            <div className="mt-6 space-y-3 text-sm text-slate-700">
              <p>
                <span className="font-semibold">Organisation:</span> {intent.orgLegalName}
              </p>
              <p>
                <span className="font-semibold">Subdomain:</span> {intent.subdomain}.birdmansuite.com
              </p>
              <p>
                <span className="font-semibold">Plan:</span> {intent.plan}
              </p>
              <p>
                <span className="font-semibold">Status:</span>{' '}
                <span className="font-mono text-teal-800">{intent.status}</span>
              </p>
              <p className="text-[var(--bm-landing-muted)]">
                {STATUS_COPY[intent.status] || intent.status}
              </p>
              {intent.provisionedAppUrl && (
                <a
                  href={intent.provisionedAppUrl}
                  className="btn btn-primary mt-4 inline-flex justify-center"
                >
                  Open your instance
                </a>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-4 text-sm">
            <Link to="/login" className="brand-link font-semibold">
              Back to login
            </Link>
            <Link to="/signup" className="text-slate-500 hover:text-slate-800">
              Start another signup
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
